import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import ImageCropModal from '../components/ImageCropModal'
import { useTranslation } from '../i18n/LanguageContext'
import { compressImage } from '../utils/compressImage'

// Colors use CSS design-system variables — same as CRM, Orders, etc.
const GREEN  = 'var(--success)'
const RED    = 'var(--error)'

const BUSINESS_TYPES = [
  'Sarees','Clothing','Jewellery','Ethnic wear','Kurtis & Tops',
  'Food & Homemade','Bakery & Sweets','Handicrafts','Pottery & Ceramics',
  'Electronics','Mobile accessories','Beauty & Skincare','Haircare',
  'Home & Lifestyle','Furniture','Plants & Gardening','Accessories',
  'Bags & Purses','Footwear','Books & Stationery','Kids & Baby',
  'Toys & Games','Sports & Fitness','Organic & Natural','Pet products',
  'Art & Paintings','Photography','Education & Courses','Other',
]

const ACCENT_PRESETS = [
  { c: '#C9A96E', l: 'Gold'       }, { c: '#B08D8D', l: 'Dusty rose' },
  { c: '#7C9BB5', l: 'Steel blue' }, { c: '#8DAF8D', l: 'Sage'       },
  { c: '#A08DB5', l: 'Lavender'   }, { c: '#C4896A', l: 'Terracotta' },
  { c: '#6B8F8F', l: 'Teal'       }, { c: '#B5A080', l: 'Sand'       },
  { c: '#1877F2', l: 'Blue'       }, { c: '#0a0a0a', l: 'Black'      },
  { c: '#6366F1', l: 'Indigo'     }, { c: '#10B981', l: 'Emerald'    },
]

const RIBBON_PRESETS = [
  { c: '#0a0a0a', l: 'Black'  }, { c: '#1877F2', l: 'Blue'    },
  { c: '#6366F1', l: 'Indigo' }, { c: '#C9A96E', l: 'Gold'    },
  { c: '#B08D8D', l: 'Rose'   }, { c: '#0f766e', l: 'Teal'    },
  { c: '#7C3AED', l: 'Purple' }, { c: '#DC2626', l: 'Red'     },
]

const H_FONTS = [
  { k: 'playfair',  l: 'Playfair Display',  d: 'Classic serif — elegant, luxury',         preview: 'Playfair Display' },
  { k: 'cormorant', l: 'Cormorant Garamond', d: 'Refined serif — fashion, lifestyle',      preview: 'Cormorant Garamond' },
  { k: 'dm_serif',  l: 'DM Serif Display',   d: 'Modern serif — clean, editorial',         preview: 'DM Serif Display' },
  { k: 'fraunces',  l: 'Fraunces',           d: 'Expressive serif — bold, distinctive',    preview: 'Fraunces' },
]

const B_FONTS = [
  { k: 'inter',   l: 'Inter',       d: 'Clean & readable — most popular', preview: 'Inter' },
  { k: 'dm_sans', l: 'DM Sans',     d: 'Geometric & modern',              preview: 'DM Sans' },
  { k: 'outfit',  l: 'Outfit',      d: 'Friendly & approachable',         preview: 'Outfit' },
  { k: 'nunito',  l: 'Nunito Sans', d: 'Rounded & warm',                  preview: 'Nunito Sans' },
]

const SH_FONTS = [
  { k: 'dm_sans', l: 'DM Sans',    d: 'Clean & modern — most popular',  preview: 'DM Sans' },
  { k: 'inter',   l: 'Inter',      d: 'Neutral & versatile',            preview: 'Inter' },
  { k: 'lora',    l: 'Lora',       d: 'Elegant serif — pairs with Playfair', preview: 'Lora' },
  { k: 'raleway', l: 'Raleway',    d: 'Stylish & geometric',            preview: 'Raleway' },
  { k: 'outfit',  l: 'Outfit',     d: 'Friendly & approachable',        preview: 'Outfit' },
  { k: 'nunito',  l: 'Nunito Sans',d: 'Rounded & warm',                 preview: 'Nunito Sans' },
]

const FONT_SIZES = [
  { k: 'sm', l: 'Small' }, { k: 'md', l: 'Medium' },
  { k: 'lg', l: 'Large' }, { k: 'xl', l: 'XL' },
]

const ICONS_LIST = [
  { k:'star',  l:'★ Quality'   }, { k:'truck', l:'🚚 Delivery'  },
  { k:'check', l:'✓ Exchange'  }, { k:'crdt',  l:'💳 Payment'   },
  { k:'usrs',  l:'👥 Served'   }, { k:'shld',  l:'🛡 Trust'     },
  { k:'hrt',   l:'♥ Care'      }, { k:'gift',  l:'🎁 Gift'      },
  { k:'awd',   l:'🏆 Award'    }, { k:'lck',   l:'🔒 Secure'    },
  { k:'ref',   l:'↺ Returns'   }, { k:'ph',    l:'📞 Phone'     },
  { k:'wa',    l:'💬 Whatsapp' }, { k:'mail',  l:'✉ Email'      },
  { k:'clk',   l:'⏰ Hours'    }, { k:'pkg',   l:'📦 Package'   },
]

const COUNTRY_CODES = [
  { c: '+880', l: 'BD +880' }, { c: '+91',  l: 'IN +91'  },
  { c: '+92',  l: 'PK +92'  }, { c: '+94',  l: 'LK +94'  },
  { c: '+977', l: 'NP +977' }, { c: '+1',   l: 'US +1'   },
  { c: '+44',  l: 'GB +44'  }, { c: '+61',  l: 'AU +61'  },
  { c: '+971', l: 'AE +971' }, { c: '+966', l: 'SA +966' },
  { c: '+60',  l: 'MY +60'  }, { c: '+65',  l: 'SG +65'  },
]

const CROP_CONFIGS = {
  cover:     { aspect: 3,   title: 'Crop cover photo',      recommendedSize: '1440 × 480px landscape' },
  logo:      { aspect: 1,   title: 'Crop logo',             recommendedSize: '400 × 400px square' },
  logo_dark: { aspect: 1,   title: 'Crop dark logo',        recommendedSize: '400 × 400px square' },
  founder:   { aspect: 3/4, title: 'Crop founder photo',    recommendedSize: '500 × 667px portrait' },
  about_main:{ aspect: 4/3, title: 'Crop about main photo', recommendedSize: '900 × 600px landscape' },
  about_sm:  { aspect: 3/4, title: 'Crop about side photo', recommendedSize: '600 × 800px portrait' },
  gallery0:  { aspect: 3/4, title: 'Crop gallery photo 1',  recommendedSize: '600 × 800px portrait' },
  gallery1:  { aspect: 1,   title: 'Crop gallery photo 2',  recommendedSize: '600 × 600px square' },
  gallery2:  { aspect: 1,   title: 'Crop gallery photo 3',  recommendedSize: '600 × 600px square' },
  gallery3:  { aspect: 4/3, title: 'Crop gallery photo 4',  recommendedSize: '900 × 600px landscape' },
  gallery4:  { aspect: 4/3, title: 'Crop gallery photo 5',  recommendedSize: '900 × 600px landscape' },
  gallery5:  { aspect: 1,   title: 'Crop gallery photo 6',  recommendedSize: '600 × 600px square' },
  product0:  { aspect: 3/4, title: 'Crop product 1 (hero)', recommendedSize: '600 × 800px portrait' },
  product1:  { aspect: 3/4, title: 'Crop product 2',        recommendedSize: '600 × 800px portrait' },
  product2:  { aspect: 3/4, title: 'Crop product 3',        recommendedSize: '600 × 800px portrait' },
  product3:  { aspect: 1,   title: 'Crop product 4',        recommendedSize: '600 × 600px square' },
  product4:  { aspect: 1,   title: 'Crop product 5',        recommendedSize: '600 × 600px square' },
}

const getCropConfig = (type, index) => {
  const key = index !== null ? `${type}${index}` : type
  return CROP_CONFIGS[key] || CROP_CONFIGS[type] || { aspect: 1, title: 'Crop photo', recommendedSize: '600 × 600px' }
}

const GFONTS = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Cormorant+Garamond:wght@700&family=DM+Serif+Display&family=Fraunces:wght@700&family=Inter:wght@400;600&family=DM+Sans:wght@400;600&family=Outfit:wght@400;600&family=Nunito+Sans:wght@400;600&display=swap'

const inp = {
  width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
  padding: '9px 12px', fontSize: '12.5px', outline: 'none',
  background: 'var(--bg-card)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)',
  boxSizing: 'border-box', transition: 'border-color 0.15s',
}
const sel = { ...inp, cursor: 'pointer' }

function CharCount({ value, max }) {
  const len  = (value || '').length
  const over = len > max
  return <span style={{ fontSize: '10.5px', color: over ? RED : '#B0B3B8', float: 'right' }}>{len}/{max}</span>
}

function Field({ label, optional, hint, children, tip, charCount, charMax, t }) {
  const [tipOpen, setTipOpen] = useState(false)
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
          <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {label}
            {optional && <span style={{ fontWeight: 400, color: '#B0B3B8' }}>{t ? t('wb.optional') : 'optional'}</span>}
            {tip && (
              <button onClick={() => setTipOpen(o => !o)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--primary)', padding: 0, lineHeight: 1 }}
                title="Why this matters">ⓘ</button>
            )}
          </label>
          {charMax && <CharCount value={charCount} max={charMax} />}
        </div>
      )}
      {tipOpen && tip && (
        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '11.5px', color: '#1a1a2e', lineHeight: 1.6, marginBottom: '8px' }}>
          💡 {tip}
        </div>
      )}
      {children}
      {hint && <div style={{ fontSize: '11px', color: '#B0B3B8', marginTop: '4px', lineHeight: 1.5 }}>{hint}</div>}
    </div>
  )
}

function Inp({ label, optional, hint, tip, type = 'text', value, onChange, placeholder, rows, max, t }) {
  const isArea = rows !== undefined
  const el = isArea
    ? <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} maxLength={max}
        style={{ ...inp, resize: 'vertical' }}
        onFocus={e => e.target.style.borderColor = 'var(--primary)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'} />
    : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} maxLength={max}
        style={{ ...inp }}
        onFocus={e => e.target.style.borderColor = 'var(--primary)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'} />
  return (
    <Field label={label} optional={optional} hint={hint} tip={tip} charCount={value} charMax={max} t={t}>
      {el}
    </Field>
  )
}

function PhoneField({ label, optional, hint, tip, codeVal, onCodeChange, numVal, onNumChange, placeholder, t }) {
  return (
    <Field label={label} optional={optional} hint={hint} tip={tip} t={t}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <select value={codeVal} onChange={e => onCodeChange(e.target.value)}
          style={{ ...sel, width: '110px', flexShrink: 0 }}
          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}>
          {COUNTRY_CODES.map(cc => <option key={cc.c} value={cc.c}>{cc.l}</option>)}
        </select>
        <input type="tel" value={numVal || ''} onChange={e => onNumChange(e.target.value)}
          placeholder={placeholder || '01XXXXXXXXX'}
          style={{ ...inp, flex: 1 }}
          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'} />
      </div>
    </Field>
  )
}

function SectionCard({ title, icon, tip, children, tLabel }) {
  const [tipOpen, setTipOpen] = useState(false)
  return (
    <div className='pc-fade-up' style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginBottom: '14px' }}>
      <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          {icon && <span style={{ fontSize: '14px' }}>{icon}</span>}
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        </div>
        {tip && (
          <button onClick={() => setTipOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-sm)', transition: 'background 0.12s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#E6FAF6'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            {tLabel || 'ⓘ Why this section?'}
          </button>
        )}
      </div>
      {tipOpen && tip && (
        <div style={{ padding: '12px 16px', background: 'var(--primary-light)', borderBottom: '1px solid var(--primary-border)', fontSize: '12px', color: '#1a1a2e', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--primary)' }}>💡 Why this matters:</strong> {tip}
        </div>
      )}
      <div style={{ padding: '16px' }}>{children}</div>
    </div>
  )
}

function Grid2({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
      {children}
    </div>
  )
}

function WebsiteBuilder() {
  const { seller } = useAuth()
  const { t }      = useTranslation()

  const coverRef    = useRef()
  const logoRef     = useRef()
  const founderRef  = useRef()
  const galleryRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()]
  const productRefs = [useRef(), useRef(), useRef(), useRef(), useRef()]
  const aboutRefs   = [useRef(), useRef(), useRef()]

  const parseJson = (str, fallback) => { try { return str ? JSON.parse(str) : fallback } catch { return fallback } }
  const blankProduct     = () => ({ name: '', description: '', price: '', photo_url: '' })
  const blankTestimonial = () => ({ name: '', location: '', text: '', stars: 5 })

  const blankFaq = () => ({ q: '', a: '' })

// ── WEBSITE TEMPLATES ──
// Each template is a complete locked set: layout + palette + fonts.
// Picking one auto-applies all values to form state.
const TEMPLATES = [
  // ── HERO SHOWCASE LAYOUT ──
  {
    key: 'hero_boutique_gold',
    name: 'Boutique Gold',
    layout: 'hero',
    desc: 'Elegant serif — classic luxury boutique feel',
    accent: '#C9A96E', bg: '#ffffff', section_alt: '#fafafa', footer_bg: '#0a0a0a', text: '#111111',
    font_heading: 'playfair', font_subheading: 'dm_sans', font_body: 'inter',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#0a0a0a',
    tags: ['Sarees','Jewellery','Ethnic wear','Clothing','Kurtis & Tops','Accessories','Bags & Purses'],
    preview: { h: '#C9A96E', bg1: '#fff', bg2: '#fafafa', dark: '#0a0a0a' },
  },
  {
    key: 'hero_editorial_rose',
    name: 'Editorial Rose',
    layout: 'hero',
    desc: 'Refined Cormorant — soft editorial, fashion-forward',
    accent: '#B08D8D', bg: '#fdf8f6', section_alt: '#f5ede9', footer_bg: '#1a1210', text: '#2d1a15',
    font_heading: 'cormorant', font_subheading: 'dm_sans', font_body: 'dm_sans',
    font_size_heading: 'xl', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#2d1a15',
    tags: ['Sarees','Clothing','Ethnic wear','Kurtis & Tops','Footwear'],
    preview: { h: '#B08D8D', bg1: '#fdf8f6', bg2: '#f5ede9', dark: '#1a1210' },
  },
  {
    key: 'hero_dark_luxury',
    name: 'Dark Luxury',
    layout: 'hero',
    desc: 'Fraunces on black — high-end, dramatic, bold',
    accent: '#C9A96E', bg: '#0a0a0a', section_alt: '#111111', footer_bg: '#050505', text: '#f5f0e8',
    font_heading: 'fraunces', font_subheading: 'raleway', font_body: 'inter',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#C9A96E',
    tags: ['Jewellery','Accessories','Beauty & Skincare','Perfume','Bags & Purses'],
    preview: { h: '#C9A96E', bg1: '#0a0a0a', bg2: '#111111', dark: '#050505' },
  },
  {
    key: 'hero_modern_minimal',
    name: 'Clean & Modern',
    layout: 'hero',
    desc: 'Sora sans-serif — sharp, contemporary, versatile',
    accent: '#0a0a0a', bg: '#ffffff', section_alt: '#f5f5f5', footer_bg: '#0a0a0a', text: '#111111',
    font_heading: 'sora', font_subheading: 'inter', font_body: 'inter',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#0a0a0a',
    tags: ['Electronics','Mobile accessories','Toys & Games','Sports & Fitness','Books & Stationery'],
    preview: { h: '#0a0a0a', bg1: '#fff', bg2: '#f5f5f5', dark: '#0a0a0a' },
  },
  {
    key: 'hero_bold_navy',
    name: 'Bold Navy',
    layout: 'hero',
    desc: 'Navy & teal — confident, trustworthy, professional',
    accent: '#2DD4B0', bg: '#ffffff', section_alt: '#f8fafc', footer_bg: '#0B1F3A', text: '#0f172a',
    font_heading: 'sora', font_subheading: 'dm_sans', font_body: 'dm_sans',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#0B1F3A',
    tags: ['Electronics','Beauty & Skincare','Haircare','Home & Lifestyle','Organic & Natural','Pet products'],
    preview: { h: '#2DD4B0', bg1: '#fff', bg2: '#f8fafc', dark: '#0B1F3A' },
  },
  {
    key: 'hero_fresh_green',
    name: 'Fresh & Natural',
    layout: 'hero',
    desc: 'Earthy greens — honest, fresh, wholesome',
    accent: '#3D7A5E', bg: '#f8fdf9', section_alt: '#edf7f1', footer_bg: '#0d2118', text: '#0f2d1e',
    font_heading: 'fraunces', font_subheading: 'outfit', font_body: 'outfit',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#3D7A5E',
    tags: ['Food & Homemade','Organic & Natural','Plants & Gardening','Bakery & Sweets'],
    preview: { h: '#3D7A5E', bg1: '#f8fdf9', bg2: '#edf7f1', dark: '#0d2118' },
  },
  {
    key: 'hero_warm_terracotta',
    name: 'Homemade Warmth',
    layout: 'hero',
    desc: 'Terracotta warmth — personal, cosy, artisan',
    accent: '#C4896A', bg: '#fdf8f4', section_alt: '#f5ede5', footer_bg: '#1a100a', text: '#2d1a0e',
    font_heading: 'dm_serif', font_subheading: 'nunito', font_body: 'nunito',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#C4896A',
    tags: ['Food & Homemade','Bakery & Sweets','Home & Lifestyle','Handicrafts','Pottery & Ceramics','Art & Paintings'],
    preview: { h: '#C4896A', bg1: '#fdf8f4', bg2: '#f5ede5', dark: '#1a100a' },
  },
  {
    key: 'hero_soft_lavender',
    name: 'Soft Lavender',
    layout: 'hero',
    desc: 'Lavender & Cormorant — gentle, feminine, approachable',
    accent: '#A08DB5', bg: '#faf8fd', section_alt: '#f2eef8', footer_bg: '#1a1520', text: '#1a1025',
    font_heading: 'cormorant', font_subheading: 'nunito', font_body: 'nunito',
    font_size_heading: 'xl', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#A08DB5',
    tags: ['Beauty & Skincare','Haircare','Kids & Baby','Accessories','Footwear'],
    preview: { h: '#A08DB5', bg1: '#faf8fd', bg2: '#f2eef8', dark: '#1a1520' },
  },
  {
    key: 'hero_indigo_fashion',
    name: 'Indigo Fashion',
    layout: 'hero',
    desc: 'Deep indigo — stylish, urban, confident',
    accent: '#6366F1', bg: '#ffffff', section_alt: '#f8f8ff', footer_bg: '#1e1b4b', text: '#1e1b4b',
    font_heading: 'playfair', font_subheading: 'raleway', font_body: 'inter',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#6366F1',
    tags: ['Clothing','Footwear','Bags & Purses','Accessories','Jewellery'],
    preview: { h: '#6366F1', bg1: '#fff', bg2: '#f8f8ff', dark: '#1e1b4b' },
  },
  {
    key: 'hero_crimson_heritage',
    name: 'Crimson Heritage',
    layout: 'hero',
    desc: 'Rich crimson — traditional, festive, cultural',
    accent: '#C0392B', bg: '#fdf9f7', section_alt: '#f9f0ee', footer_bg: '#1a0a08', text: '#2d0f0a',
    font_heading: 'playfair', font_subheading: 'lora', font_body: 'lora',
    font_size_heading: 'lg', font_size_subheading: 'md', font_size_body: 'md',
    ribbon: '#C0392B',
    tags: ['Sarees','Ethnic wear','Handicrafts','Pottery & Ceramics','Art & Paintings'],
    preview: { h: '#C0392B', bg1: '#fdf9f7', bg2: '#f9f0ee', dark: '#1a0a08' },
  },
]

// Helper — given seller's business_types, rank templates by relevance
function rankTemplates(bizTypes) {
  if (!bizTypes || bizTypes.length === 0) return TEMPLATES
  return [...TEMPLATES].sort((a, b) => {
    const scoreA = a.tags.filter(t => bizTypes.includes(t)).length
    const scoreB = b.tags.filter(t => bizTypes.includes(t)).length
    return scoreB - scoreA
  })
}


  const [form, setForm] = useState({
    shop_name: seller?.shop_name || '', tagline: seller?.tagline || '',
    website_template: seller?.website_template || 'custom',
    hero_subtitle: seller?.hero_subtitle || '',
    about_short: seller?.about_short || '', about_long: seller?.about_long || '',
    about_section_heading: seller?.about_section_heading || '',
    owner_name: seller?.owner_name || '', owner_bio: seller?.owner_bio || '',
    founder_photo_url: seller?.founder_photo_url || '',
    founder_heading: seller?.founder_heading || '',
    founder_sub: seller?.founder_sub || '',
    facebook_url: seller?.facebook_url || '', instagram_url: seller?.instagram_url || '',
    youtube_url: seller?.youtube_url || '', linkedin_url: seller?.linkedin_url || '',
    tiktok_url: seller?.tiktok_url || '', whatsapp_number: seller?.whatsapp_number || '',
    whatsapp_country_code: seller?.whatsapp_country_code || '+880',
    phone: seller?.phone || '', phone_country_code: seller?.phone_country_code || '+880',
    shop_email: seller?.shop_email || '', website_url: seller?.website_url || '',
    district: seller?.district || '', shop_address: seller?.shop_address || '',
    delivery_partners: seller?.delivery_partners || '', delivery_radius: seller?.delivery_radius || '',
    delivery_timeline: seller?.delivery_timeline || '', payment_info: seller?.payment_info || '',
    current_offer: seller?.current_offer || '', offer_expiry: seller?.offer_expiry || '',
    business_types: parseJson(seller?.business_types, []),
    custom_business_type: '',
    cover_photo_url: seller?.cover_photo_url || '', logo_url: seller?.logo_url || '',
    gallery_photos: parseJson(seller?.gallery_photos, Array(6).fill('')),
    about_photos: parseJson(seller?.about_photos, ['', '', '']),
    theme_accent:      parseJson(seller?.theme_colors, {}).accent      || '#C9A96E',
    ribbon_color:      seller?.ribbon_color || '#0a0a0a',
    font_heading:      seller?.font_heading || 'playfair',
    font_body:         seller?.font_body || 'inter',
    stats_orders:      seller?.stats_orders || '1,000+',
    stats_customers:   seller?.stats_customers || '800+',
    stats_rating:      seller?.stats_rating || '4.9 ★',
    stats_years:       seller?.stats_years || '2+',
    featured_products: parseJson(seller?.featured_products, [blankProduct(), blankProduct(), blankProduct()]),
    testimonials:      parseJson(seller?.testimonials, [blankTestimonial(), blankTestimonial(), blankTestimonial()]),
    about_bullets:     parseJson(seller?.about_bullets, ['Authentic quality, carefully selected', 'Nationwide delivery with full tracking', 'Personal service from a real team']),
    why_us_cards:      parseJson(seller?.why_us_cards, [
      { icon:'star',  title:'Authentic quality',    text:'Every product personally selected. No mass production, no compromise on quality.' },
      { icon:'truck', title:'Nationwide delivery',  text:'Dhaka 2–3 days, nationwide 5–7 days. Packed with care, delivered reliably.' },
      { icon:'check', title:'Personal service',     text:'Real people, real replies. No bots, no wait times.' },
      { icon:'crdt',  title:'Easy exchange',        text:'Not satisfied? Easy exchange within 7 days, no questions asked.' },
      { icon:'crdt',  title:'Flexible payment',     text:'bKash, Nagad, bank transfer or cash on delivery — your choice.' },
      { icon:'usrs',  title:'Thousands served',     text:'Thousands of orders delivered across Bangladesh.' },
    ]),
    why_heading: seller?.why_heading || '',
    why_sub:     seller?.why_sub     || '',
    collection_heading: seller?.collection_heading || '',
    collection_sub:     seller?.collection_sub     || '',
    collection_cta:     seller?.collection_cta     || '',
    reviews_heading: seller?.reviews_heading || '',
    contact_heading: seller?.contact_heading || '',
    contact_sub:     seller?.contact_sub     || '',
    business_hours:  seller?.business_hours  || '',
    faq_heading: seller?.faq_heading || '',
    faqs: parseJson(seller?.faqs, [blankFaq(), blankFaq(), blankFaq()]),
    payment_badges:    parseJson(seller?.payment_badges, []),
    delivery_badges:   parseJson(seller?.delivery_badges, []),
    show_trust_badges: seller?.show_trust_badges !== false,
    gallery_section_title: seller?.gallery_section_title || 'A glimpse of our work',
    logo_dark_url:         seller?.logo_dark_url || '',
    font_subheading:       seller?.font_subheading || 'dm_sans',
    font_size_heading:     seller?.font_size_heading || 'lg',
    font_size_subheading:  seller?.font_size_subheading || 'md',
    font_size_body:        seller?.font_size_body || 'md',
    bg_color:           (() => { try { return seller?.theme_colors ? JSON.parse(seller.theme_colors).bg           || '#ffffff' : '#ffffff' } catch { return '#ffffff' } })(),
    section_alt_color:  (() => { try { return seller?.theme_colors ? JSON.parse(seller.theme_colors).section_alt  || '#fafafa' : '#fafafa' } catch { return '#fafafa' } })(),
    footer_bg_color:    (() => { try { return seller?.theme_colors ? JSON.parse(seller.theme_colors).footer_bg    || '#050505' : '#050505' } catch { return '#050505' } })(),
    text_color:         (() => { try { return seller?.theme_colors ? JSON.parse(seller.theme_colors).text         || '#111111' : '#111111' } catch { return '#111111' } })(),
  })

  const [saving, setSaving]         = useState(false)
  const [saved, setSaved]           = useState(false)
  const [uploading, setUploading]   = useState('')
  const [activeTab, setActiveTab]   = useState('content')
  const [copied, setCopied]         = useState(false)
  const [cropModal, setCropModal]   = useState(null)
  const [analytics, setAnalytics]   = useState(null)
  const [loadingAnalytics, setLoadingAnalytics] = useState(false)
  const [viewChart, setViewChart]   = useState([])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Instantly apply a template's full palette+font set to form state
  const applyTemplate = (tpl) => {
    if (tpl === 'custom') { set('website_template', 'custom'); return }
    setForm(f => ({
      ...f,
      website_template:    tpl.key,
      theme_accent:        tpl.accent,
      bg_color:            tpl.bg,
      section_alt_color:   tpl.section_alt,
      footer_bg_color:     tpl.footer_bg,
      text_color:          tpl.text,
      font_heading:        tpl.font_heading,
      font_subheading:     tpl.font_subheading,
      font_body:           tpl.font_body,
      font_size_heading:   tpl.font_size_heading,
      font_size_subheading:tpl.font_size_subheading,
      font_size_body:      tpl.font_size_body,
      ribbon_color:        tpl.ribbon,
    }))
  }
  const setProduct = (i, k, v) => setForm(f => { const p = [...f.featured_products]; p[i] = { ...p[i], [k]: v }; return { ...f, featured_products: p } })
  const setBullet  = (i, v) => setForm(f => { const b = [...f.about_bullets]; b[i] = v; return { ...f, about_bullets: b } })
  const addBullet  = () => { if (form.about_bullets.length < 5) setForm(f => ({ ...f, about_bullets: [...f.about_bullets, ''] })) }
  const removeBullet = i => setForm(f => ({ ...f, about_bullets: f.about_bullets.filter((_, idx) => idx !== i) }))
  const setWhyCard   = (i, k, v) => setForm(f => { const w = [...f.why_us_cards]; w[i] = { ...w[i], [k]: v }; return { ...f, why_us_cards: w } })
  const setTestimonial = (i, k, v) => setForm(f => { const arr = [...f.testimonials]; arr[i] = { ...arr[i], [k]: v }; return { ...f, testimonials: arr } })
  const addProduct    = () => { if (form.featured_products.length < 10) setForm(f => ({ ...f, featured_products: [...f.featured_products, blankProduct()] })) }
  const removeProduct = i => setForm(f => ({ ...f, featured_products: f.featured_products.filter((_, idx) => idx !== i) }))
  const addTestimonial    = () => { if (form.testimonials.length < 5) setForm(f => ({ ...f, testimonials: [...f.testimonials, blankTestimonial()] })) }
  const removeTestimonial = i => setForm(f => ({ ...f, testimonials: f.testimonials.filter((_, idx) => idx !== i) }))
  const setFaq = (i, k, v) => setForm(f => { const arr = [...f.faqs]; arr[i] = { ...arr[i], [k]: v }; return { ...f, faqs: arr } })
  const addFaqItem    = () => { if (form.faqs.length < 8) setForm(f => ({ ...f, faqs: [...f.faqs, blankFaq()] })) }
  const removeFaqItem = i => setForm(f => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }))
  const togglePayBadge = b => set('payment_badges',  form.payment_badges.includes(b)  ? form.payment_badges.filter(x => x !== b)  : [...form.payment_badges, b])
  const toggleDelBadge = b => set('delivery_badges', form.delivery_badges.includes(b) ? form.delivery_badges.filter(x => x !== b) : [...form.delivery_badges, b])

  useEffect(() => {
    if (!document.getElementById('wb-gfonts')) {
      const l = document.createElement('link'); l.id = 'wb-gfonts'; l.rel = 'stylesheet'; l.href = GFONTS
      document.head.appendChild(l)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'analytics' && seller?.id) fetchAnalytics()
  }, [activeTab, seller]) // eslint-disable-line

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true)
    const { data: allViews } = await supabase.from('shop_page_views')
      .select('viewed_at, referrer').eq('seller_id', seller.id)
      .order('viewed_at', { ascending: false })

    if (!allViews || allViews.length === 0) {
      setAnalytics({ total: 0, today: 0, week: 0, prevWeek: 0, month: 0, prevMonth: 0, bestDay: null, bestHour: null, fbRefs: 0 })
      setViewChart([]); setLoadingAnalytics(false); return
    }

    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const start7d   = new Date(now - 7  * 86400000)
    const start14d  = new Date(now - 14 * 86400000)
    const start30d  = new Date(now - 30 * 86400000)
    const start60d  = new Date(now - 60 * 86400000)

    const total     = allViews.length
    const today     = allViews.filter(v => new Date(v.viewed_at) >= startOfToday).length
    const week      = allViews.filter(v => new Date(v.viewed_at) >= start7d).length
    const prevWeek  = allViews.filter(v => new Date(v.viewed_at) >= start14d && new Date(v.viewed_at) < start7d).length
    const month     = allViews.filter(v => new Date(v.viewed_at) >= start30d).length
    const prevMonth = allViews.filter(v => new Date(v.viewed_at) >= start60d && new Date(v.viewed_at) < start30d).length
    const fbRefs    = allViews.filter(v => v.referrer && v.referrer.toLowerCase().includes('facebook')).length

    const dayMap = {}; const hourMap = {}
    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
    allViews.forEach(v => {
      const d = new Date(v.viewed_at)
      const day = DAYS[d.getDay()]; const hr = d.getHours()
      dayMap[day] = (dayMap[day] || 0) + 1
      hourMap[hr] = (hourMap[hr] || 0) + 1
    })
    const bestDay  = Object.entries(dayMap).sort((a, b) => b[1] - a[1])[0]
    const bestHour = Object.entries(hourMap).sort((a, b) => b[1] - a[1])[0]

    const chart = []
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now - i * 86400000)
      const ds = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const de = new Date(ds.getTime() + 86400000)
      chart.push({ label: d.toLocaleDateString('en-BD', { day: 'numeric', month: 'short' }), value: allViews.filter(v => { const vd = new Date(v.viewed_at); return vd >= ds && vd < de }).length })
    }
    setViewChart(chart)
    setAnalytics({ total, today, week, prevWeek, month, prevMonth, bestDay: bestDay?.[0], bestHour: bestHour?.[0], fbRefs })
    setLoadingAnalytics(false)
  }

  const shopUsername = seller?.facebook_username || seller?.facebook_url?.split('/').filter(Boolean).pop() || 'yourshop'
  const shopUrl      = `app.pageconnect.io/web/${shopUsername}`

  const handleCopy = () => {
    navigator.clipboard.writeText('https://' + shopUrl)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  const toggleBizType = type => set('business_types', form.business_types.includes(type)
    ? form.business_types.filter(tp => tp !== type)
    : [...form.business_types, type])

  const addCustomBizType = () => {
    const v = form.custom_business_type.trim()
    if (!v) return
    if (!form.business_types.includes(v)) set('business_types', [...form.business_types, v])
    set('custom_business_type', '')
  }

  const handleFileSelect = async (file, type, index = null) => {
    if (!file) return
    // Compress before cropping — reduces file size 60-80% with no visible quality loss
    const compressed = await compressImage(file, type)
    setCropModal({ file: compressed, type, index })
  }

  const handleCropComplete = async croppedFile => {
    const { type, index } = cropModal; setCropModal(null)
    const key = type + (index !== null ? index : '')
    setUploading(key)
    const path = `${seller.id}/${type}-${index !== null ? index : Date.now()}.jpg`
    const { error } = await supabase.storage.from('shop-assets').upload(path, croppedFile, { upsert: true, contentType: 'image/jpeg', cacheControl: '3600' })
    if (!error) {
      const { data: u } = supabase.storage.from('shop-assets').getPublicUrl(path)
      const url = u.publicUrl + '?t=' + Date.now()
      if (type === 'cover')      set('cover_photo_url', url)
      if (type === 'logo')       set('logo_url', url)
      if (type === 'logo_dark')  set('logo_dark_url', url)
      if (type === 'founder')    set('founder_photo_url', url)
      if (type === 'gallery')    setForm(f => { const p = [...f.gallery_photos]; p[index] = url; return { ...f, gallery_photos: p } })
      if (type === 'about_main') setForm(f => { const p = [...f.about_photos]; p[0]     = url; return { ...f, about_photos: p } })
      if (type === 'about_sm')   setForm(f => { const p = [...f.about_photos]; p[index] = url; return { ...f, about_photos: p } })
      if (type === 'product')    setProduct(index, 'photo_url', url)
    }
    setUploading('')
  }

  const handleSave = async () => {
    setSaving(true)
    await supabase.from('sellers').update({
      shop_name: form.shop_name, tagline: form.tagline,
      about_short: form.about_short, about_long: form.about_long,
      owner_name: form.owner_name, owner_bio: form.owner_bio,
      founder_photo_url: form.founder_photo_url,
      facebook_url: form.facebook_url, instagram_url: form.instagram_url,
      youtube_url: form.youtube_url, linkedin_url: form.linkedin_url,
      tiktok_url: form.tiktok_url, whatsapp_number: form.whatsapp_number,
      whatsapp_country_code: form.whatsapp_country_code,
      phone: form.phone, phone_country_code: form.phone_country_code,
      shop_email: form.shop_email, website_url: form.website_url,
      district: form.district, shop_address: form.shop_address,
      delivery_partners: form.delivery_partners, delivery_radius: form.delivery_radius,
      delivery_timeline: form.delivery_timeline, payment_info: form.payment_info,
      current_offer: form.current_offer, offer_expiry: form.offer_expiry,
      business_types: JSON.stringify(form.business_types),
      cover_photo_url: form.cover_photo_url, logo_url: form.logo_url,
      gallery_photos: JSON.stringify(form.gallery_photos.filter(Boolean)),
      about_photos: JSON.stringify(form.about_photos),
      theme_colors: JSON.stringify({ accent: form.theme_accent, bg: form.bg_color, section_alt: form.section_alt_color, footer_bg: form.footer_bg_color, text: form.text_color }),
      ribbon_color: form.ribbon_color,
      font_heading: form.font_heading, font_body: form.font_body,
      stats_orders: form.stats_orders, stats_customers: form.stats_customers,
      stats_rating: form.stats_rating, stats_years: form.stats_years,
      featured_products: JSON.stringify(form.featured_products.filter(p => p.name)),
      about_bullets: JSON.stringify(form.about_bullets.filter(Boolean)),
      why_us_cards: JSON.stringify(form.why_us_cards),
      gallery_section_title: form.gallery_section_title,
      logo_dark_url: form.logo_dark_url,
      font_subheading: form.font_subheading,
      font_size_heading: form.font_size_heading,
      font_size_subheading: form.font_size_subheading,
      font_size_body: form.font_size_body,
      testimonials: JSON.stringify(form.testimonials.filter(tmn => tmn.name && tmn.text)),
      hero_subtitle: form.hero_subtitle,
      about_section_heading: form.about_section_heading,
      founder_heading: form.founder_heading,
      founder_sub: form.founder_sub,
      why_heading: form.why_heading,
      why_sub: form.why_sub,
      collection_heading: form.collection_heading,
      collection_sub: form.collection_sub,
      collection_cta: form.collection_cta,
      reviews_heading: form.reviews_heading,
      contact_heading: form.contact_heading,
      contact_sub: form.contact_sub,
      business_hours: form.business_hours,
      faq_heading: form.faq_heading,
      faqs: JSON.stringify(form.faqs.filter(f => f.q && f.a)),
      payment_badges: JSON.stringify(form.payment_badges),
      delivery_badges: JSON.stringify(form.delivery_badges),
      show_trust_badges: form.show_trust_badges,
      website_template: form.website_template,
    }).eq('id', seller?.id)
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000)
  }

  const accent = form.theme_accent || '#C9A96E'

  const TABS = [
    { k: 'template',  l: 'Template',             ic: '🎨' },
    { k: 'content',   l: t('wb.tab_content'),    ic: '✏️' },
    { k: 'products',  l: t('wb.tab_products'),   ic: '🛍️' },
    { k: 'reviews',   l: t('wb.tab_reviews'),    ic: '⭐' },
    { k: 'website',   l: 'Website',              ic: '🌐' },
    { k: 'media',     l: t('wb.tab_media'),      ic: '🖼️' },
    { k: 'design',    l: t('wb.tab_design'),     ic: '🎨' },
    { k: 'analytics', l: t('wb.tab_analytics'),  ic: '📊' },
  ]

  const trendArrow = (curr, prev) => {
    if (!prev) return null
    const pct = Math.round(((curr - prev) / prev) * 100)
    return <span style={{ fontSize: '11px', fontWeight: 700, color: pct >= 0 ? GREEN : RED, marginLeft: '6px' }}>{pct >= 0 ? '↑' : '↓'}{Math.abs(pct)}%</span>
  }

  const UploadSlot = ({ src, onClick, isLoading, height = 140, ph, radius = 'var(--radius-md)', contain = false, square = false, aspectRatioOverride = null }) => (
    <div onClick={onClick}
      style={{ width: '100%', height: (square || aspectRatioOverride) ? 'auto' : height, aspectRatio: aspectRatioOverride || (square ? '1 / 1' : 'auto'), borderRadius: radius, border: '2px dashed var(--border)', overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {!src && <div style={{ textAlign: 'center', pointerEvents: 'none' }}><div style={{ fontSize: '20px', marginBottom: '4px' }}>📷</div><div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>{ph}</div></div>}
      {src && <img src={src + '?t=1'} alt="" style={{ width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover', objectPosition: 'center top', pointerEvents: 'none' }} />}
      {src && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'} />}
      {isLoading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: '18px', height: '18px', border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'wb-spin 0.7s linear infinite' }} /></div>}
    </div>
  )

  const whyTipLabel = t('wb.why_tip_btn')

  return (
    <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-page)', fontFamily: 'var(--font-ui)' }}>
      <Topbar title={t('wb.title')} />
      <style>{`@keyframes wb-spin { to { transform: rotate(360deg) } } @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } } .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; } .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; } .pc-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(11,31,58,0.08) !important; } @media(max-width:640px){ .wb-grid{grid-template-columns:1fr!important} .wb-grid>*{grid-column:span 1!important} }`}</style>
      <div style={{ padding: '18px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('wb.header_title')}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('wb.header_sub')}</div>
          </div>
          <button onClick={() => window.open(`https://app.pageconnect.io/web/${shopUsername}`, '_blank')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
            {t('wb.preview_btn')}
          </button>
        </div>

        {/* URL bar */}
        <div style={{ background: 'var(--primary)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>{t('wb.url_label')}</div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>https://{shopUrl}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button onClick={handleCopy} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', background: copied ? GREEN : 'rgba(255,255,255,0.1)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer', transition: 'background 0.2s' }}>
              {copied ? t('wb.copied') : t('wb.copy_link')}
            </button>
            <button onClick={() => window.open(`https://app.pageconnect.io/web/${shopUsername}`, '_blank')} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
              {t('wb.open_btn')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-card)', border: '1px solid var(--border)', marginBottom: '16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
          {TABS.map(tab => (
            <button key={tab.k} onClick={() => setActiveTab(tab.k)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.k ? 'var(--primary)' : 'transparent', color: activeTab === tab.k ? 'var(--bg-card)' : 'var(--text-muted)', transition: 'all 0.12s', whiteSpace: 'nowrap', flexShrink: 0 }}>
              <span style={{ fontSize: '13px' }}>{tab.ic}</span><span>{tab.l}</span>
            </button>
          ))}
        </div>

        {/* ── TEMPLATE TAB ── */}
        {activeTab === 'template' && (() => {
          const ranked = rankTemplates(form.business_types)
          const suggested = ranked.slice(0, 3)
          const rest      = ranked.slice(3)
          const active    = form.website_template || 'custom'

          const TemplateCard = ({ tpl, isSuggested }) => {
            const isActive = active === (tpl === 'custom' ? 'custom' : tpl.key)
            const p = tpl === 'custom' ? null : tpl.preview
            return (
              <div
                className="pc-card-hover"
                onClick={() => applyTemplate(tpl)}
                style={{ borderRadius: 'var(--radius-lg)', border: isActive ? '2px solid var(--primary)' : '1px solid var(--border)', background: 'var(--bg-card)', overflow: 'hidden', cursor: 'pointer', boxShadow: isActive ? '0 0 0 3px var(--primary-border)' : 'var(--shadow-sm)', transition: 'border 0.15s, box-shadow 0.15s' }}>

                {/* Mini website preview */}
                {tpl !== 'custom' ? (
                  <div style={{ height: '110px', position: 'relative', overflow: 'hidden', background: p.bg1 }}>
                    {/* Fake nav */}
                    <div style={{ height: '18px', background: p.dark, display: 'flex', alignItems: 'center', padding: '0 8px', gap: '4px' }}>
                      <div style={{ width: '18px', height: '8px', borderRadius: '2px', background: 'rgba(255,255,255,0.25)' }} />
                      <div style={{ flex: 1 }} />
                      {[1,2,3].map(i => <div key={i} style={{ width: '16px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.2)' }} />)}
                    </div>
                    {/* Fake hero */}
                    <div style={{ height: '56px', background: `linear-gradient(135deg, ${p.dark} 0%, ${p.h}55 100%)`, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 10px', gap: '4px' }}>
                      <div style={{ width: '55%', height: '6px', borderRadius: '3px', background: '#fff', opacity: 0.9 }} />
                      <div style={{ width: '70%', height: '4px', borderRadius: '2px', background: '#fff', opacity: 0.5 }} />
                      <div style={{ marginTop: '4px', display: 'flex', gap: '5px' }}>
                        <div style={{ width: '36px', height: '10px', borderRadius: '2px', background: p.h }} />
                        <div style={{ width: '30px', height: '10px', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.4)' }} />
                      </div>
                    </div>
                    {/* Fake stats row */}
                    <div style={{ height: '16px', background: p.bg2, display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '0 8px' }}>
                      {[1,2,3,4].map(i => <div key={i} style={{ width: '14px', height: '6px', borderRadius: '2px', background: p.dark, opacity: 0.15 }} />)}
                    </div>
                    {/* Fake section */}
                    <div style={{ height: '20px', background: p.bg1, display: 'flex', alignItems: 'center', padding: '0 10px', gap: '6px' }}>
                      <div style={{ width: '30%', height: '5px', borderRadius: '2px', background: p.dark, opacity: 0.12 }} />
                      <div style={{ flex: 1 }} />
                      <div style={{ width: '16px', height: '5px', borderRadius: '2px', background: p.h, opacity: 0.7 }} />
                    </div>
                    {/* Active badge */}
                    {isActive && (
                      <div style={{ position: 'absolute', top: '22px', right: '7px', background: 'var(--primary)', color: '#fff', fontSize: '8px', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-full)', letterSpacing: '0.06em' }}>ACTIVE</div>
                    )}
                    {isSuggested && !isActive && (
                      <div style={{ position: 'absolute', top: '22px', right: '7px', background: 'var(--success)', color: '#fff', fontSize: '8px', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-full)', letterSpacing: '0.06em' }}>SUGGESTED</div>
                    )}
                    {/* Accent color dot */}
                    <div style={{ position: 'absolute', bottom: '18px', left: '10px', width: '8px', height: '8px', borderRadius: '50%', background: p.h, border: '1.5px solid rgba(255,255,255,0.6)' }} />
                  </div>
                ) : (
                  <div style={{ height: '110px', background: 'var(--bg-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>Current settings</div>
                    {isActive && (
                      <div style={{ background: 'var(--primary)', color: '#fff', fontSize: '8px', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-full)', letterSpacing: '0.06em' }}>ACTIVE</div>
                    )}
                  </div>
                )}

                {/* Card info */}
                <div style={{ padding: '10px 12px 12px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px' }}>
                    {tpl === 'custom' ? 'Custom' : tpl.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    {tpl === 'custom' ? 'Keep your existing custom colors & fonts' : tpl.desc}
                  </div>
                  {tpl !== 'custom' && (
                    <div style={{ display: 'flex', gap: '4px', marginTop: '8px', alignItems: 'center' }}>
                      {[tpl.accent, tpl.bg === '#ffffff' ? '#f0f0f0' : tpl.bg, tpl.section_alt, tpl.footer_bg].map((c, i) => (
                        <div key={i} style={{ width: '12px', height: '12px', borderRadius: '50%', background: c, border: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }} />
                      ))}
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '4px', fontStyle: 'italic' }}>
                        {tpl.font_heading.charAt(0).toUpperCase() + tpl.font_heading.slice(1).replace('_',' ')} · {tpl.font_body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          }

          return (
            <>
              {/* Suggested for your business */}
              {form.business_types.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
                    ✦ Suggested for {form.business_types.slice(0,2).join(', ')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {suggested.map(tpl => <TemplateCard key={tpl.key} tpl={tpl} isSuggested={true} />)}
                  </div>
                </div>
              )}

              {/* All templates */}
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
                  {form.business_types.length > 0 ? 'All templates' : 'Choose a template'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                  {(form.business_types.length > 0 ? rest : ranked).map(tpl => <TemplateCard key={tpl.key} tpl={tpl} isSuggested={false} />)}
                  <TemplateCard tpl="custom" isSuggested={false} />
                </div>
              </div>

              {/* Info banner */}
              <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>Picking a template instantly applies</strong> its colors and fonts. You can still fine-tune everything in the <strong>Design</strong> tab afterwards. Save to make it live on your website.
              </div>
            </>
          )
        })()}

        {/* ── CONTENT TAB ── */}
        {activeTab === 'content' && (
          <>
            <SectionCard title={t('wb.shop_identity')} icon="🏪" tip={t('wb.shop_identity_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <Inp label={t('wb.shop_name') + ' *'} value={form.shop_name} onChange={v => set('shop_name', v)} placeholder={t('wb.shop_name_ph')} t={t} />
                <Inp label="Hero tagline *" value={form.tagline} onChange={v => set('tagline', v)} placeholder="e.g. Handcrafted sarees from the heart of Rajshahi" max={100} tip="This is the BIG headline on your website homepage — the first thing visitors read. Make it bold, specific, and memorable." t={t} />
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label="Hero subtitle" optional rows={2} value={form.hero_subtitle} onChange={v => set('hero_subtitle', v)} placeholder="e.g. Authentic fabrics, nationwide delivery, personal service — since 2021" max={200} tip="Shown below your tagline on the hero section. A short sentence that expands on your headline — what you do, where you deliver, why you're different." t={t} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.about_long')} optional rows={5} value={form.about_long} onChange={v => set('about_long', v)} placeholder={t('wb.about_long_ph')} max={600} tip={t('wb.about_long_tip')} t={t} />
                </div>
              </Grid2>

              <SectionCard title={t('wb.bullets_title')} icon="✓" tip={t('wb.bullets_tip')} tLabel={whyTipLabel}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                  {form.about_bullets.map((b, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '12px', fontWeight: 700, color: 'var(--primary)' }}>{i + 1}</div>
                      <input value={b} onChange={e => setBullet(i, e.target.value)} placeholder={t('wb.bullet_ph')}
                        style={{ ...inp, flex: 1 }}
                        onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                      {form.about_bullets.length > 1 && (
                        <button onClick={() => removeBullet(i)} style={{ width: '28px', height: '28px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', cursor: 'pointer', color: RED, fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                {form.about_bullets.length < 5 && (
                  <button onClick={addBullet} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                    {t('wb.add_bullet')}
                  </button>
                )}
              </SectionCard>

              <Field label={t('wb.biz_type_label')} tip={t('wb.biz_type_tip')} t={t}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '10px' }}>
                  {BUSINESS_TYPES.map(type => (
                    <button key={type} onClick={() => toggleBizType(type)}
                      style={{ padding: '5px 13px', borderRadius: 'var(--radius-full)', border: '1px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s', background: form.business_types.includes(type) ? 'var(--text-primary)' : 'var(--bg-card)', color: form.business_types.includes(type) ? 'var(--bg-card)' : 'var(--text-muted)', borderColor: form.business_types.includes(type) ? 'var(--text-primary)' : 'var(--border)' }}>
                      {type}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={form.custom_business_type} onChange={e => set('custom_business_type', e.target.value)}
                    placeholder={t('wb.custom_biz_ph')} style={{ ...inp, flex: 1 }}
                    onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    onKeyDown={e => e.key === 'Enter' && addCustomBizType()} />
                  <button onClick={addCustomBizType} style={{ padding: '9px 16px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '12.5px', fontWeight: 600, color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                    {t('wb.add_btn')}
                  </button>
                </div>
              </Field>
            </SectionCard>

            <SectionCard title={t('wb.stats_title')} icon="📊" tip={t('wb.stats_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <Inp label={t('wb.stats_orders')} value={form.stats_orders} onChange={v => set('stats_orders', v)} placeholder={t('wb.stats_orders_ph')} t={t} />
                <Inp label={t('wb.stats_customers')} value={form.stats_customers} onChange={v => set('stats_customers', v)} placeholder={t('wb.stats_customers_ph')} t={t} />
                <Inp label={t('wb.stats_rating')} value={form.stats_rating} onChange={v => set('stats_rating', v)} placeholder={t('wb.stats_rating_ph')} t={t} />
                <Inp label={t('wb.stats_years')} value={form.stats_years} onChange={v => set('stats_years', v)} placeholder={t('wb.stats_years_ph')} t={t} />
              </Grid2>
            </SectionCard>

            <SectionCard title={t('wb.why_us')} icon="🏆" tip={t('wb.why_us_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {form.why_us_cards.map((w, i) => (
                  <div key={i} style={{ background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px' }}>{t('wb.why_icon')}</div>
                        <select value={w.icon || 'star'} onChange={e => setWhyCard(i, 'icon', e.target.value)}
                          style={{ ...inp, fontSize: '11.5px' }}
                          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                          onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                          {ICONS_LIST.map(ic => <option key={ic.k} value={ic.k}>{ic.l}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px' }}>{t('wb.why_card_title')}</div>
                        <input value={w.title || ''} onChange={e => setWhyCard(i, 'title', e.target.value)}
                          placeholder={t('wb.why_card_title_ph')} style={{ ...inp }}
                          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                          onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                      </div>
                    </div>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px' }}>{t('wb.why_desc')}</div>
                    <textarea rows={2} value={w.text || ''} onChange={e => setWhyCard(i, 'text', e.target.value)}
                      placeholder={t('wb.why_desc_ph')} style={{ ...inp, resize: 'none' }}
                      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.gallery_heading_title')} icon="🖼️" tip={t('wb.gallery_heading_tip')} tLabel={whyTipLabel}>
              <Inp label={t('wb.gallery_heading_label')} value={form.gallery_section_title} onChange={v => set('gallery_section_title', v)} placeholder={t('wb.gallery_heading_ph')} t={t} />
            </SectionCard>

            <SectionCard title={t('wb.offer_title')} tip={t('wb.offer_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.offer_details')} optional rows={2} value={form.current_offer} onChange={v => set('current_offer', v)} placeholder={t('wb.offer_ph')} max={200} tip={t('wb.offer_tip2')} t={t} />
                </div>
                <Inp label={t('wb.offer_expiry')} optional value={form.offer_expiry} onChange={v => set('offer_expiry', v)} placeholder={t('wb.offer_expiry_ph')} t={t} />
              </Grid2>
            </SectionCard>

            <SectionCard title={t('wb.social_title')} icon="🔗" tip={t('wb.social_tip')} tLabel={whyTipLabel}>
              <Inp label={t('wb.facebook_url') + ' *'} value={form.facebook_url} onChange={v => set('facebook_url', v)} placeholder={t('wb.facebook_url_ph')} tip={t('wb.facebook_tip')} hint={t('wb.facebook_hint')} t={t} />
              <Grid2>
                <PhoneField label={t('wb.whatsapp')} optional codeVal={form.whatsapp_country_code} onCodeChange={v => set('whatsapp_country_code', v)} numVal={form.whatsapp_number} onNumChange={v => set('whatsapp_number', v)} placeholder="01XXXXXXXXX" tip={t('wb.whatsapp_tip')} t={t} />
                <PhoneField label={t('wb.phone')} optional codeVal={form.phone_country_code} onCodeChange={v => set('phone_country_code', v)} numVal={form.phone} onNumChange={v => set('phone', v)} placeholder="01XXXXXXXXX" t={t} />
                <Inp label={t('wb.email')} optional type="email" value={form.shop_email} onChange={v => set('shop_email', v)} placeholder={t('wb.email_ph')} tip={t('wb.email_tip')} t={t} />
                <Inp label={t('wb.instagram')} optional value={form.instagram_url} onChange={v => set('instagram_url', v)} placeholder={t('wb.instagram_ph')} t={t} />
                <Inp label={t('wb.tiktok')} optional value={form.tiktok_url} onChange={v => set('tiktok_url', v)} placeholder={t('wb.tiktok_ph')} t={t} />
                <Inp label={t('wb.youtube')} optional value={form.youtube_url} onChange={v => set('youtube_url', v)} placeholder={t('wb.youtube_ph')} t={t} />
                <Inp label={t('wb.linkedin')} optional value={form.linkedin_url} onChange={v => set('linkedin_url', v)} placeholder={t('wb.linkedin_ph')} t={t} />
                <Inp label={t('wb.other_website')} optional value={form.website_url} onChange={v => set('website_url', v)} placeholder={t('wb.other_website_ph')} t={t} />
              </Grid2>
            </SectionCard>

            <SectionCard title={t('wb.location_title')} icon="📍" tip={t('wb.location_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <Inp label={t('wb.district')} optional value={form.district} onChange={v => set('district', v)} placeholder={t('wb.district_ph')} t={t} />
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.full_address')} optional rows={2} value={form.shop_address} onChange={v => set('shop_address', v)} placeholder={t('wb.full_address_ph')} t={t} />
                </div>
              </Grid2>
            </SectionCard>

            <SectionCard title={t('wb.delivery_title')} icon="🚚" tip={t('wb.delivery_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <Inp label={t('wb.delivery_partners')} optional value={form.delivery_partners} onChange={v => set('delivery_partners', v)} placeholder={t('wb.delivery_partners_ph')} t={t} />
                <Inp label={t('wb.delivery_area')} optional value={form.delivery_radius} onChange={v => set('delivery_radius', v)} placeholder={t('wb.delivery_area_ph')} t={t} />
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.delivery_timeline')} optional rows={2} value={form.delivery_timeline} onChange={v => set('delivery_timeline', v)} placeholder={t('wb.delivery_timeline_ph')} t={t} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.payment_methods')} optional rows={2} value={form.payment_info} onChange={v => set('payment_info', v)} placeholder={t('wb.payment_methods_ph')} t={t} />
                </div>
              </Grid2>
            </SectionCard>

            <SectionCard title={t('wb.founder_title')} icon="👤" tip={t('wb.founder_tip')} tLabel={whyTipLabel}>
              <Grid2>
                <Inp label={t('wb.your_name')} optional value={form.owner_name} onChange={v => set('owner_name', v)} placeholder={t('wb.your_name_ph')} t={t} />
                <div style={{ gridColumn: 'span 2' }}>
                  <Inp label={t('wb.your_story')} optional rows={5} value={form.owner_bio} onChange={v => set('owner_bio', v)} placeholder={t('wb.your_story_ph')} max={500} tip={t('wb.your_story_tip')} t={t} />
                </div>
              </Grid2>
              <div style={{ marginTop: '4px' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>{t('wb.founder_photo')} <span style={{ fontWeight: 400, color: '#B0B3B8' }}>{t('wb.optional')}</span></div>
                <div style={{ width: '100%', aspectRatio: '3/4', borderRadius: 'var(--radius-md)', border: '2px dashed var(--border)', overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '260px' }}
                  onClick={() => founderRef.current?.click()}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  {form.founder_photo_url ? <img src={form.founder_photo_url + '?t=1'} alt="founder" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} /> : (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>📷</div>
                      <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('wb.upload_founder_ph')}</div>
                      <div style={{ fontSize: '10.5px', color: '#B0B3B8', marginTop: '2px' }}>{t('wb.founder_portrait_hint')}</div>
                    </div>
                  )}
                  {uploading === 'founder' && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: '18px', height: '18px', border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'wb-spin 0.7s linear infinite' }} /></div>}
                </div>
                <input ref={founderRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'founder')} />
                {form.founder_photo_url && (
                  <button onClick={() => set('founder_photo_url', '')} style={{ marginTop: '6px', padding: '5px 12px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11.5px', color: RED, cursor: 'pointer', fontWeight: 600 }}>{t('wb.remove')}</button>
                )}
              </div>
            </SectionCard>
          </>
        )}

        {/* ── PRODUCTS TAB ── */}
        {activeTab === 'products' && (
          <SectionCard title={t('wb.products_title')} icon="🛍️" tip={t('wb.products_tip')} tLabel={whyTipLabel}>
            {form.featured_products.map((p, i) => (
              <div key={i} className='pc-card-hover' style={{ background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '14px', marginBottom: '12px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('wb.product_num')} {i + 1}</div>
                  {form.featured_products.length > 1 && (
                    <button onClick={() => removeProduct(i)} style={{ padding: '4px 10px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11.5px', color: RED, cursor: 'pointer', fontWeight: 600 }}>{t('wb.product_remove')}</button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', alignItems: 'start' }}>
                  <div>
                    <UploadSlot src={p.photo_url} onClick={() => productRefs[i]?.current?.click()} isLoading={uploading === `product${i}`} height={0} square={i >= 3} aspectRatioOverride={i >= 3 ? '1/1' : '3/4'} ph={t('wb.product_name')} />
                    <input ref={productRefs[i]} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'product', i)} />
                  </div>
                  <div>
                    <Inp label={t('wb.product_name') + ' *'} value={p.name} onChange={v => setProduct(i, 'name', v)} placeholder={t('wb.product_name_ph')} max={80} t={t} />
                    <Inp label={t('wb.product_desc')} optional value={p.description} onChange={v => setProduct(i, 'description', v)} placeholder={t('wb.product_desc_ph')} max={100} t={t} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end' }}>
                      <Inp label={t('wb.product_price')} optional value={p.price} onChange={v => setProduct(i, 'price', v)} placeholder={t('wb.product_price_ph')} t={t} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '14px' }}>
                        <input type="checkbox" id={'sp-' + i} checked={p.show_price !== false} onChange={e => setProduct(i, 'show_price', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <label htmlFor={'sp-' + i} style={{ fontSize: '11.5px', color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{t('wb.product_show_price')}</label>
                      </div>
                    </div>
                    <Inp label={t('wb.product_fb_url')} optional value={p.fb_post_url || ''} onChange={v => setProduct(i, 'fb_post_url', v)} placeholder={t('wb.product_fb_url_ph')} hint={t('wb.product_fb_url_hint')} t={t} />
                  </div>
                </div>
              </div>
            ))}
            {form.featured_products.length < 10 && (
              <button onClick={addProduct} style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                {t('wb.add_product')}
              </button>
            )}
            <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '11.5px', color: 'var(--primary)', lineHeight: 1.6 }}>
              {t('wb.products_note')}
            </div>
          </SectionCard>
        )}

        {/* ── REVIEWS TAB ── */}
        {activeTab === 'reviews' && (
          <SectionCard title={t('wb.reviews_title')} icon="⭐" tip={t('wb.reviews_tip')} tLabel={whyTipLabel}>
            <div style={{ marginBottom: '14px' }}>
              <Inp label="Section heading" optional value={form.reviews_heading} onChange={v => set('reviews_heading', v)} placeholder='e.g. What our customers say.' t={t} />
            </div>
            {form.testimonials.map((tmn, i) => (
              <div key={i} className='pc-card-hover' style={{ background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '14px', marginBottom: '12px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('wb.review_num')} {i + 1}</div>
                  {form.testimonials.length > 1 && (
                    <button onClick={() => removeTestimonial(i)} style={{ padding: '4px 10px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11.5px', color: RED, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                  )}
                </div>
                <Grid2>
                  <Inp label={t('wb.customer_name') + ' *'} value={tmn.name} onChange={v => setTestimonial(i, 'name', v)} placeholder={t('wb.customer_name_ph')} t={t} />
                  <Inp label={t('wb.customer_location')} optional value={tmn.location} onChange={v => setTestimonial(i, 'location', v)} placeholder={t('wb.customer_location_ph')} t={t} />
                  <div style={{ gridColumn: 'span 2' }}>
                    <Inp label={t('wb.review_text') + ' *'} rows={3} value={tmn.text} onChange={v => setTestimonial(i, 'text', v)} placeholder={t('wb.review_text_ph')} max={200} tip={t('wb.review_tip')} t={t} />
                  </div>
                  <div>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px' }}>{t('wb.star_rating')}</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[5, 4, 3].map(n => (
                        <button key={n} onClick={() => setTestimonial(i, 'stars', n)}
                          style={{ padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: tmn.stars === n ? 'var(--warning)' : 'var(--bg-card)', color: tmn.stars === n ? 'var(--bg-card)' : 'var(--text-muted)', borderColor: tmn.stars === n ? 'var(--warning)' : 'var(--border)' }}>
                          {'★'.repeat(n)}
                        </button>
                      ))}
                    </div>
                  </div>
                </Grid2>
              </div>
            ))}
            {form.testimonials.length < 5 && (
              <button onClick={addTestimonial} style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                + Add review (up to 5)
              </button>
            )}
          </SectionCard>
        )}

        {/* ── WEBSITE TAB ── FAQ, Trust Badges, section headings, contact copy */}
        {activeTab === 'website' && (
          <>
            {/* ── Section headings ── */}
            <SectionCard title="Section headings & copy" icon="✏️" tip="Customise every section heading and subtext on your website. Defaults look great — only change if you want something specific." tLabel={whyTipLabel}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>About section</div>
                  <Inp label="Heading label" optional value={form.about_section_heading} onChange={v => set('about_section_heading', v)} placeholder='e.g. Our Story' t={t} />
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Collection section</div>
                  <Grid2>
                    <Inp label="Section heading" optional value={form.collection_heading} onChange={v => set('collection_heading', v)} placeholder='e.g. Featured Collection' t={t} />
                    <Inp label="Facebook CTA button" optional value={form.collection_cta} onChange={v => set('collection_cta', v)} placeholder='e.g. See full collection on Facebook' t={t} />
                    <div style={{ gridColumn: 'span 2' }}>
                      <Inp label="Subtext under heading" optional rows={2} value={form.collection_sub} onChange={v => set('collection_sub', v)} placeholder='e.g. A selection of our favourites — visit our Facebook page for the full collection.' t={t} />
                    </div>
                  </Grid2>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Why us section</div>
                  <Grid2>
                    <Inp label="Heading" optional value={form.why_heading} onChange={v => set('why_heading', v)} placeholder='e.g. Why choose us' t={t} />
                    <Inp label="Subheading" optional value={form.why_sub} onChange={v => set('why_sub', v)} placeholder='e.g. What sets us apart.' t={t} />
                  </Grid2>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Founder section</div>
                  <Grid2>
                    <Inp label="Main heading" optional value={form.founder_heading} onChange={v => set('founder_heading', v)} placeholder='e.g. Built from passion.' t={t} />
                    <Inp label="Second line" optional value={form.founder_sub} onChange={v => set('founder_sub', v)} placeholder='e.g. Grown through trust.' t={t} />
                  </Grid2>
                </div>
                <div style={{ padding: '12px 0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Contact section</div>
                  <Grid2>
                    <Inp label="Heading" optional value={form.contact_heading} onChange={v => set('contact_heading', v)} placeholder="e.g. Let's talk." t={t} />
                    <Inp label="Business hours" optional value={form.business_hours} onChange={v => set('business_hours', v)} placeholder='e.g. Sat – Thu, 9am – 9pm' t={t} />
                    <div style={{ gridColumn: 'span 2' }}>
                      <Inp label="Subtext" optional rows={2} value={form.contact_sub} onChange={v => set('contact_sub', v)} placeholder='e.g. We typically reply within 30 minutes during business hours.' t={t} />
                    </div>
                  </Grid2>
                </div>
              </div>
            </SectionCard>

            {/* ── FAQ ── */}
            <SectionCard title="FAQ — Frequently Asked Questions" icon="❓" tip="Add questions your customers commonly ask. These appear as an accordion on your website. Up to 8 Q&A pairs." tLabel={whyTipLabel}>
              <div style={{ marginBottom: '14px' }}>
                <Inp label="Section heading" optional value={form.faq_heading} onChange={v => set('faq_heading', v)} placeholder='e.g. Frequently asked questions' t={t} />
              </div>
              {form.faqs.map((faq, i) => (
                <div key={i} style={{ background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '14px', marginBottom: '10px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>Q&A {i + 1}</div>
                    <button onClick={() => removeFaqItem(i)} style={{ padding: '3px 10px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11.5px', color: RED, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                  </div>
                  <Inp label="Question" value={faq.q} onChange={v => setFaq(i, 'q', v)} placeholder='e.g. How long does delivery take?' t={t} />
                  <Inp label="Answer" rows={3} value={faq.a} onChange={v => setFaq(i, 'a', v)} placeholder='e.g. Dhaka deliveries take 1–2 days, outside Dhaka 3–5 days depending on the courier.' t={t} />
                </div>
              ))}
              {form.faqs.length < 8 && (
                <button onClick={addFaqItem} style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', border: '1.5px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  + Add question (up to 8)
                </button>
              )}
              <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '11.5px', color: 'var(--primary)', lineHeight: 1.6 }}>
                💡 Good FAQs: delivery time, payment methods, exchange/return policy, how to order, do you deliver outside Dhaka, minimum order, custom orders.
              </div>
            </SectionCard>

            {/* ── TRUST BADGES ── */}
            <SectionCard title="Trust badges — Payment & Delivery" icon="🔒" tip="Show which payment methods you accept and which courier companies you use. Builds instant trust with visitors." tLabel={whyTipLabel}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Show trust badges on website</div>
                <button onClick={() => set('show_trust_badges', !form.show_trust_badges)}
                  style={{ width: '44px', height: '24px', borderRadius: 'var(--radius-lg)', border: 'none', cursor: 'pointer', background: form.show_trust_badges ? 'var(--primary)' : 'var(--border)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'var(--bg-card)', position: 'absolute', top: '3px', left: form.show_trust_badges ? '23px' : '3px', transition: 'left 0.2s' }} />
                </button>
              </div>

              {form.show_trust_badges && (
                <>
                  {/* Payment badges */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Payment methods you accept</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {[
                        { k: 'bKash',            color: '#E2F5ED', text: '#0E7A46', border: '#A3DBBD', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#E2136E"/><text x="20" y="26" textAnchor="middle" fontSize="18" fontWeight="800" fill="#fff" fontFamily="sans-serif">b</text></svg>
                        )},
                        { k: 'Nagad',            color: '#FFF0E5', text: '#D44506', border: '#FFBC99', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#F05A28"/><text x="20" y="26" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="sans-serif">N</text></svg>
                        )},
                        { k: 'Rocket',           color: '#F0E6F9', text: '#6B21A8', border: '#D8B4FE', icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B21A8" strokeWidth="1.8" strokeLinecap="round"><path d="M9.5 14.5L3 21M12 2C6 8 6 16 12 22c6-6 6-14 0-20z"/><circle cx="12" cy="12" r="2"/></svg>
                        )},
                        { k: 'Cash on delivery', color: '#F0FDF4', text: '#166534', border: '#86EFAC', icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M12 12m-2 0a2 2 0 104 0 2 2 0 10-4 0"/><path d="M6 12h.01M18 12h.01"/></svg>
                        )},
                        { k: 'Card',             color: '#EFF6FF', text: '#1D4ED8', border: '#93C5FD', icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        )},
                        { k: 'Bank transfer',    color: '#F8FAFC', text: '#475569', border: '#CBD5E1', icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.8" strokeLinecap="round"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M8 10v11M12 10v11M16 10v11M20 10v11"/></svg>
                        )},
                      ].map(({ k, color, text, border, icon }) => {
                        const active = form.payment_badges.includes(k)
                        return (
                          <button key={k} onClick={() => togglePayBadge(k)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: 'var(--radius-full)', border: '2px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s', background: active ? color : 'var(--bg-card)', color: active ? text : 'var(--text-muted)', borderColor: active ? border : 'var(--border)' }}>
                            {icon}
                            {k}
                            {active && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={text} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Delivery badges */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Courier / delivery partners</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {[
                        { k: 'Pathao',     color: '#FFF7ED', text: '#C2410C', border: '#FED7AA', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#F97316"/><text x="20" y="26" textAnchor="middle" fontSize="16" fontWeight="800" fill="#fff" fontFamily="sans-serif">P</text></svg>
                        )},
                        { k: 'Steadfast', color: '#ECFDF5', text: '#065F46', border: '#6EE7B7', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#10B981"/><text x="20" y="26" textAnchor="middle" fontSize="16" fontWeight="800" fill="#fff" fontFamily="sans-serif">S</text></svg>
                        )},
                        { k: 'Sundarban', color: '#EFF6FF', text: '#1D4ED8', border: '#93C5FD', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#2563EB"/><text x="20" y="26" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="sans-serif">SN</text></svg>
                        )},
                        { k: 'RedX',      color: '#FEF2F2', text: '#B91C1C', border: '#FCA5A5', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#DC2626"/><text x="20" y="26" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="sans-serif">Rx</text></svg>
                        )},
                        { k: 'eCourier', color: '#F0FDF4', text: '#15803D', border: '#86EFAC', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#16A34A"/><text x="20" y="26" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="sans-serif">eC</text></svg>
                        )},
                        { k: 'SA Paribahan', color: '#FFF7ED', text: '#92400E', border: '#FCD34D', icon: (
                          <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#D97706"/><text x="20" y="26" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="sans-serif">SA</text></svg>
                        )},
                        { k: 'Own delivery', color: '#F8FAFC', text: '#475569', border: '#CBD5E1', icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 4v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                        )},
                      ].map(({ k, color, text, border, icon }) => {
                        const active = form.delivery_badges.includes(k)
                        return (
                          <button key={k} onClick={() => toggleDelBadge(k)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: 'var(--radius-full)', border: '2px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s', background: active ? color : 'var(--bg-card)', color: active ? text : 'var(--text-muted)', borderColor: active ? border : 'var(--border)' }}>
                            {icon}
                            {k}
                            {active && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={text} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </SectionCard>
          </>
        )}

        {/* ── MEDIA TAB ── */}
        {activeTab === 'media' && (
          <>
            {/* Layout guide */}
            <div className='pc-fade-up' style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <span style={{ fontSize: '14px' }}>📐</span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('wb.media_guide_title')}</span>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t('wb.media_guide_sub')}</span>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                  {[
                    { label: 'Hero banner',          ratio: '3:1 landscape', size: '1440×480px',  color: '#E6F1FB', border: '#B5D4F4', text: '#0C447C', icon: '🖼️' },
                    { label: 'About — main photo',   ratio: '4:3 landscape', size: '900×600px',   color: '#E6F1FB', border: '#B5D4F4', text: '#0C447C', icon: '🏞️' },
                    { label: 'About — 2 side photos',ratio: '3:4 portrait',  size: '600×800px',   color: '#EEEDFE', border: '#AFA9EC', text: '#3C3489', icon: '👤' },
                    { label: 'Product 1 (hero)',      ratio: '3:4 portrait',  size: '600×800px',   color: '#E1F5EE', border: '#5DCAA5', text: '#085041', icon: '👗' },
                    { label: 'Products 2 & 3',        ratio: '3:4 portrait',  size: '600×800px',   color: '#E1F5EE', border: '#9FE1CB', text: '#085041', icon: '👗' },
                    { label: 'Products 4 & 5',        ratio: '1:1 square',    size: '600×600px',   color: '#EAF3DE', border: '#97C459', text: '#27500A', icon: '💍' },
                    { label: 'Gallery photo 1',       ratio: '3:4 portrait',  size: '600×800px',   color: '#FBEAF0', border: '#ED93B1', text: '#72243E', icon: '📸' },
                    { label: 'Gallery 2 & 3',         ratio: '1:1 square',    size: '600×600px',   color: '#FBEAF0', border: '#F4C0D1', text: '#72243E', icon: '🔲' },
                    { label: 'Gallery 4 & 5',         ratio: '4:3 landscape', size: '900×600px',   color: '#FAEEDA', border: '#FAC775', text: '#633806', icon: '🏞️' },
                    { label: 'Founder photo',         ratio: '3:4 portrait',  size: '500×667px',   color: '#F1EFE8', border: '#D3D1C7', text: '#444441', icon: '🤳' },
                    { label: 'Logo',                  ratio: '1:1 square',    size: '400×400px',   color: '#F1EFE8', border: '#D3D1C7', text: '#444441', icon: '🏪' },
                  ].map((item, i) => (
                    <div key={i} style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: item.color, border: '1px solid ' + item.border, display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: item.text, marginBottom: '2px' }}>{item.label}</div>
                        <div style={{ fontSize: '10.5px', color: item.text, opacity: 0.7 }}>{item.ratio} · {item.size}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '9px 12px', borderRadius: 'var(--radius-md)', background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '11.5px', color: '#16A34A', lineHeight: 1.6 }}>
                  {t('wb.media_guide_tip')}
                </div>
              </div>
            </div>

            <SectionCard title={t('wb.hero_title')} icon="🖼️" tip={t('wb.hero_tip')} tLabel={whyTipLabel}>
              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px' }}>{t('wb.hero_sub')}</div>
              <UploadSlot src={form.cover_photo_url} onClick={() => coverRef.current?.click()} isLoading={uploading === 'cover'} height={140} ph={t('wb.hero_ph')} />
              <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'cover')} />
              {form.cover_photo_url && <button onClick={() => set('cover_photo_url', '')} style={{ marginTop: '8px', padding: '5px 12px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11.5px', color: RED, cursor: 'pointer', fontWeight: 600 }}>{t('wb.remove')}</button>}
            </SectionCard>

            <SectionCard title={t('wb.logo_title')} icon="🏪" tip={t('wb.logo_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <UploadSlot src={form.logo_url} onClick={() => logoRef.current?.click()} isLoading={uploading === 'logo'} height={90} ph="Logo" radius="12px" contain={true} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('wb.logo_label')}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('wb.logo_sub')}</div>
                  <button onClick={() => logoRef.current?.click()} style={{ padding: '7px 14px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
                    {form.logo_url ? t('wb.change_logo') : t('wb.upload_logo')}
                  </button>
                  {form.logo_url && <button onClick={() => set('logo_url', '')} style={{ marginLeft: '8px', padding: '7px 14px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: 'none', fontSize: '12px', fontWeight: 600, color: RED, cursor: 'pointer' }}>{t('wb.remove')}</button>}
                </div>
              </div>
              <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'logo')} />
            </SectionCard>

            <SectionCard title={t('wb.dark_logo_title')} icon="🌙" tip={t('wb.dark_logo_tip')} tLabel={whyTipLabel}>
              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px' }}>{t('wb.dark_logo_sub')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>{t('wb.light_bg_logo')}</div>
                  <div style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px' }}>
                    {form.logo_url ? <img src={form.logo_url + '?t=1'} alt="light logo" style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t('wb.main_logo_ph')}</span>}
                  </div>
                  <div style={{ fontSize: '10.5px', color: '#B0B3B8', marginTop: '4px' }}>{t('wb.light_logo_used')}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>{t('wb.dark_bg_logo')}</div>
                  <div style={{ padding: '16px', background: '#0a0a0a', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px' }}>
                    {form.logo_dark_url ? <img src={form.logo_dark_url + '?t=1'} alt="dark logo" style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '11px', color: '#555' }}>{t('wb.no_dark_logo')}</span>}
                  </div>
                  <div style={{ fontSize: '10.5px', color: '#B0B3B8', marginTop: '4px' }}>{t('wb.dark_logo_used')}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button onClick={() => { const inp2 = document.createElement('input'); inp2.type = 'file'; inp2.accept = 'image/*'; inp2.onchange = e => handleFileSelect(e.target.files[0], 'logo_dark'); inp2.click() }}
                  style={{ padding: '7px 14px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
                  {form.logo_dark_url ? t('wb.change_dark_logo') : t('wb.upload_dark_logo')}
                </button>
                {form.logo_dark_url && <button onClick={() => set('logo_dark_url', '')} style={{ padding: '7px 14px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: 'none', fontSize: '12px', fontWeight: 600, color: RED, cursor: 'pointer' }}>{t('wb.remove')}</button>}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.about_photos_title')} icon="🖼️" tip={t('wb.about_photos_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px' }}>
                    {t('wb.about_main_label')} <span style={{ fontSize: '10.5px', fontWeight: 400, color: '#B0B3B8', marginLeft: '8px' }}>{t('wb.about_main_size')}</span>
                  </div>
                  <UploadSlot src={form.about_photos[0]} onClick={() => aboutRefs[0].current?.click()} isLoading={uploading === 'about0'} height={140} ph={t('wb.about_main_ph')} />
                  <input ref={aboutRefs[0]} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'about_main', 0)} />
                  {form.about_photos[0] && <button onClick={() => setForm(f => { const p = [...f.about_photos]; p[0] = ''; return { ...f, about_photos: p } })} style={{ marginTop: '5px', padding: '4px 10px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11px', color: RED, cursor: 'pointer', fontWeight: 600 }}>{t('wb.remove')}</button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[1, 2].map(i => (
                    <div key={i}>
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px' }}>
                        {t('wb.about_side_label')} {i + 1} <span style={{ fontSize: '10.5px', fontWeight: 400, color: '#B0B3B8', marginLeft: '8px' }}>{t('wb.about_side_size')}</span>
                      </div>
                      <UploadSlot src={form.about_photos[i]} onClick={() => aboutRefs[i].current?.click()} isLoading={uploading === `about${i}`} height={0} ph={t('wb.about_side_ph') + ' ' + i} square={true} />
                      <input ref={aboutRefs[i]} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'about_sm', i)} />
                      {form.about_photos[i] && <button onClick={() => setForm(f => { const p = [...f.about_photos]; p[i] = ''; return { ...f, about_photos: p } })} style={{ marginTop: '5px', padding: '4px 10px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', fontSize: '11px', color: RED, cursor: 'pointer', fontWeight: 600 }}>{t('wb.remove')}</button>}
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>

            <SectionCard title={t('wb.gallery_title')} icon="📸" tip={t('wb.gallery_tip')} tLabel={whyTipLabel}>
              <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '11.5px', color: '#1877F2', lineHeight: 1.6 }}>
                {t('wb.gallery_note')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div key={i}>
                    <div style={{ position: 'relative' }}>
                      <UploadSlot src={form.gallery_photos[i]} onClick={() => galleryRefs[i].current?.click()} isLoading={uploading === `gallery${i}`} height={0}
                        ph={['Portrait 3:4', 'Square 1:1', 'Square 1:1', 'Landscape 4:3', 'Landscape 4:3', 'Square 1:1'][i] || `Photo ${i + 1}`}
                        square={[false, true, true, false, false, true][i]}
                        aspectRatioOverride={['3/4', '1/1', '1/1', '4/3', '4/3', '1/1'][i]} />
                      {form.gallery_photos[i] && (
                        <button onClick={() => setForm(f => { const p = [...f.gallery_photos]; p[i] = ''; return { ...f, gallery_photos: p } })}
                          style={{ position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px', borderRadius: '50%', background: RED, border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      )}
                    </div>
                    <input ref={galleryRefs[i]} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0], 'gallery', i)} />
                  </div>
                ))}
              </div>
            </SectionCard>
          </>
        )}

        {/* ── DESIGN TAB ── */}
        {activeTab === 'design' && (
          <>
            <SectionCard title={t('wb.design_theme')} icon="🌓" tip={t('wb.design_theme_tip')} tLabel={whyTipLabel}>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '10px' }}>{t('wb.quick_presets')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
                  {[
                    { label: 'Classic light', bg: '#ffffff', sec: '#fafafa', footer: '#050505', text: '#111111' },
                    { label: 'Warm cream',    bg: '#fdf8f3', sec: '#f5ede3', footer: '#1a1208', text: '#2d1f0e' },
                    { label: 'Cool slate',    bg: '#f8f9fb', sec: '#eef0f5', footer: '#1a1f2e', text: '#0f172a' },
                    { label: 'Deep black',    bg: '#0a0a0a', sec: '#111111', footer: '#050505', text: '#ffffff' },
                    { label: 'Dark navy',     bg: '#0f172a', sec: '#1e293b', footer: '#050f1f', text: '#f1f5f9' },
                    { label: 'Dark green',    bg: '#0a1f0a', sec: '#112211', footer: '#060f06', text: '#d4edda' },
                  ].map((preset, i) => (
                    <button key={i} onClick={() => { set('bg_color', preset.bg); set('section_alt_color', preset.sec); set('footer_bg_color', preset.footer); set('text_color', preset.text) }}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '10px 14px', borderRadius: 'var(--radius-md)', border: form.bg_color === preset.bg ? '2px solid var(--primary)' : '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', minWidth: '90px' }}>
                      <div style={{ width: '40px', height: '24px', borderRadius: 'var(--radius-sm)', background: `linear-gradient(135deg, ${preset.bg} 50%, ${preset.footer} 50%)`, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.3 }}>{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {[
                  { label: t('wb.bg_label'),          key: 'bg_color',          hint: t('wb.bg_hint') },
                  { label: t('wb.section_alt_label'),  key: 'section_alt_color', hint: t('wb.section_alt_hint') },
                  { label: t('wb.footer_bg_label'),    key: 'footer_bg_color',   hint: t('wb.footer_bg_hint') },
                  { label: t('wb.text_color_label'),   key: 'text_color',        hint: t('wb.text_color_hint') },
                ].map(field => (
                  <div key={field.key}>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px' }}>{field.label}</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="color" value={form[field.key]} onChange={e => set(field.key, e.target.value)} style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', cursor: 'pointer', padding: '2px', flexShrink: 0 }} />
                      <input type="text" value={form[field.key]} onChange={e => set(field.key, e.target.value)} style={{ ...inp, fontFamily: 'JetBrains Mono,monospace', fontSize: '12px' }} onFocus={e => e.target.style.borderColor = 'var(--primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#B0B3B8', marginTop: '3px' }}>{field.hint}</div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.accent_title')} icon="🎨" tip={t('wb.accent_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                {ACCENT_PRESETS.map(({ c, l }) => (
                  <button key={c} onClick={() => set('theme_accent', c)} title={l}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '10px 6px', borderRadius: 'var(--radius-md)', border: form.theme_accent === c ? '2px solid var(--text-primary)' : '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: c, border: '1px solid rgba(0,0,0,0.08)' }} />
                    <span style={{ fontSize: '9.5px', color: 'var(--text-muted)' }}>{l}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', flexShrink: 0 }}>{t('wb.accent_custom')}</span>
                <input type="text" value={form.theme_accent} onChange={e => set('theme_accent', e.target.value)} placeholder={t('wb.accent_ph')} style={{ ...inp, width: '110px', fontFamily: 'JetBrains Mono, monospace' }} onFocus={e => e.target.style.borderColor = 'var(--primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <input type="color" value={form.theme_accent} onChange={e => set('theme_accent', e.target.value)} style={{ width: '40px', height: '36px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', cursor: 'pointer', padding: '2px' }} />
                <div style={{ flex: 1, minWidth: '100px', height: '36px', borderRadius: 'var(--radius-md)', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff', letterSpacing: '0.06em' }}>{t('wb.accent_preview')}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title={t('wb.ribbon_title')} icon="🎀" tip={t('wb.ribbon_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {RIBBON_PRESETS.map(({ c, l }) => (
                  <button key={c} onClick={() => set('ribbon_color', c)} title={l}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: form.ribbon_color === c ? '2px solid var(--primary)' : '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer' }}>
                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, border: '1px solid rgba(0,0,0,0.1)' }} />
                    <span style={{ fontSize: '9.5px', color: 'var(--text-muted)' }}>{l}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="color" value={form.ribbon_color} onChange={e => set('ribbon_color', e.target.value)} style={{ width: '40px', height: '36px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', cursor: 'pointer', padding: '2px' }} />
                <div style={{ flex: 1, height: '36px', borderRadius: 'var(--radius-md)', background: form.ribbon_color, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <span style={{ fontSize: '11px', color: '#fff', letterSpacing: '0.08em', whiteSpace: 'nowrap', opacity: 0.8 }}>{t('wb.ribbon_preview_text')}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title={t('wb.heading_font')} icon="🖋️" tip={t('wb.heading_font_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {H_FONTS.map(f => (
                  <button key={f.k} onClick={() => set('font_heading', f.k)}
                    style={{ padding: '14px 16px', borderRadius: 'var(--radius-md)', border: form.font_heading === f.k ? '2px solid var(--primary)' : '1px solid var(--border)', background: form.font_heading === f.k ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px', fontFamily: `'${f.preview}', serif` }}>{f.l}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.d}</div>
                  </button>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.subheading_font')} icon="🔤" tip={t('wb.subheading_font_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                {SH_FONTS.map(f => (
                  <button key={f.k} onClick={() => set('font_subheading', f.k)}
                    style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', border: form.font_subheading === f.k ? '2px solid var(--primary)' : '1px solid var(--border)', background: form.font_subheading === f.k ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '3px', fontFamily: `'${f.preview}', sans-serif` }}>{f.l}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.d}</div>
                  </button>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.font_sizes')} icon="📐" tip={t('wb.font_sizes_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: t('wb.size_heading'),    key: 'font_size_heading',    current: form.font_size_heading },
                  { label: t('wb.size_subheading'), key: 'font_size_subheading', current: form.font_size_subheading },
                  { label: t('wb.size_body'),       key: 'font_size_body',       current: form.font_size_body },
                ].map(fs => (
                  <div key={fs.key}>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>{fs.label}</div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {FONT_SIZES.map(sz => (
                        <button key={sz.k} onClick={() => set(fs.key, sz.k)}
                          style={{ flex: 1, padding: '7px 0', borderRadius: 'var(--radius-sm)', border: '1px solid', fontSize: '11px', fontWeight: 600, cursor: 'pointer', background: fs.current === sz.k ? 'var(--primary)' : 'var(--bg-card)', color: fs.current === sz.k ? 'var(--bg-card)' : 'var(--text-muted)', borderColor: fs.current === sz.k ? 'var(--primary)' : 'var(--border)' }}>
                          {sz.l}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t('wb.body_font')} icon="📝" tip={t('wb.body_font_tip')} tLabel={whyTipLabel}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                {B_FONTS.map(f => (
                  <button key={f.k} onClick={() => set('font_body', f.k)}
                    style={{ padding: '14px 16px', borderRadius: 'var(--radius-md)', border: form.font_body === f.k ? '2px solid var(--primary)' : '1px solid var(--border)', background: form.font_body === f.k ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px', fontFamily: `'${f.preview}', sans-serif` }}>{f.l}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.d}</div>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '16px', background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '20px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('wb.live_preview')}</div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: '6px', fontFamily: `'${H_FONTS.find(f => f.k === form.font_heading)?.preview}', serif` }}>
                  {form.shop_name || 'Your Shop Name'}
                </div>
                <div style={{ fontSize: '15px', color: accent, fontWeight: 600, marginBottom: '10px', fontFamily: `'${H_FONTS.find(f => f.k === form.font_heading)?.preview}', serif` }}>
                  {form.tagline || 'Your tagline appears here.'}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '14px', fontFamily: `'${B_FONTS.find(f => f.k === form.font_body)?.preview}', sans-serif` }}>
                  {form.about_short || 'Your short about text appears here, below the tagline on your hero banner.'}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ padding: '9px 20px', borderRadius: 'var(--radius-sm)', background: accent, color: '#fff', fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: `'${B_FONTS.find(f => f.k === form.font_body)?.preview}', sans-serif` }}>Browse collection</div>
                  <div style={{ padding: '9px 20px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #ddd', color: '#555', fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: `'${B_FONTS.find(f => f.k === form.font_body)?.preview}', sans-serif` }}>WhatsApp us</div>
                </div>
              </div>
            </SectionCard>
          </>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <div>
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('wb.analytics_title')}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('wb.analytics_sub')} <strong>https://{shopUrl}</strong></div>
            </div>

            {loadingAnalytics ? (
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
                <div style={{ width: '24px', height: '24px', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'wb-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('wb.analytics_loading')}</div>
              </div>
            ) : analytics && analytics.total === 0 ? (
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📊</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{t('wb.analytics_no_visits')}</div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', maxWidth: '320px', margin: '0 auto', lineHeight: 1.6 }}>{t('wb.analytics_no_visits_sub')}</div>
              </div>
            ) : analytics ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                  {[
                    { label: t('wb.kpi_total'), value: analytics.total,  color: 'var(--text-primary)',     sub: t('wb.kpi_total_sub') },
                    { label: t('wb.kpi_today'), value: analytics.today,  color: 'var(--primary)',     sub: t('wb.kpi_today_sub') },
                    { label: t('wb.kpi_week'),  value: <span>{analytics.week}{trendArrow(analytics.week, analytics.prevWeek)}</span>, color: GREEN, sub: t('wb.kpi_week_sub') },
                    { label: t('wb.kpi_month'), value: <span>{analytics.month}{trendArrow(analytics.month, analytics.prevMonth)}</span>, color: '#7C3AED', sub: t('wb.kpi_month_sub') },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: s.color }} />
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '6px' }}>{s.label}</div>
                      <div style={{ fontSize: '24px', fontWeight: 700, color: s.color, marginBottom: '2px', display: 'flex', alignItems: 'baseline' }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                  {[
                    { label: t('wb.insight_best_day'),  value: analytics.bestDay || '—', icon: '📅', sub: t('wb.insight_best_day_sub') },
                    { label: t('wb.insight_best_hour'), value: analytics.bestHour !== null ? (Number(analytics.bestHour) < 12 ? analytics.bestHour + 'am' : (Number(analytics.bestHour) === 12 ? '12pm' : (Number(analytics.bestHour) - 12) + 'pm')) : '—', icon: '⏰', sub: t('wb.insight_best_hour_sub') },
                    { label: t('wb.insight_fb'),        value: analytics.fbRefs, icon: '📘', sub: t('wb.insight_fb_sub') },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>{s.icon}</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px' }}>{s.value}</div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '2px' }}>{s.label}</div>
                      <div style={{ fontSize: '11px', color: '#C4C4C4' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {viewChart.length > 0 && (
                  <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('wb.chart_title')}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '16px' }}>{t('wb.chart_sub')}</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px', marginBottom: '6px' }}>
                      {viewChart.map((d, i) => {
                        const max = Math.max(...viewChart.map(x => x.value), 1)
                        const h   = Math.max((d.value / max) * 80, d.value > 0 ? 4 : 0)
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }} title={d.label + ': ' + d.value + ' visits'}>
                            <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: d.value > 0 ? 'var(--primary)' : 'var(--border)', height: h + 'px', transition: 'height 0.3s' }} />
                            <div style={{ fontSize: '8px', color: 'var(--text-muted)', transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap', marginTop: '4px' }}>{d.label}</div>
                          </div>
                        )
                      })}
                    </div>
                    <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '11.5px', color: 'var(--primary)', lineHeight: 1.6 }}>
                      {t('wb.chart_tip')}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Save button */}
        <div style={{ position: 'sticky', bottom: '16px', marginTop: '16px' }}>
          {saved && (
            <div style={{ padding: '10px 16px', borderRadius: 'var(--radius-md)', background: '#F0FDF4', border: '1px solid #BBF7D0', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', fontWeight: 600, color: GREEN }}>
              {t('wb.saved_msg')} <a href={`https://app.pageconnect.io/web/${shopUsername}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', marginLeft: '4px' }}>https://{shopUrl}</a>
            </div>
          )}
          <button onClick={handleSave} disabled={saving || activeTab === 'analytics'}
            style={{ width: '100%', padding: '14px', borderRadius: 'var(--radius-lg)', background: (saving || activeTab === 'analytics') ? 'var(--text-muted)' : 'var(--primary)', border: 'none', fontSize: '13.5px', fontWeight: 700, color: '#fff', cursor: (saving || activeTab === 'analytics') ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {saving ? t('wb.saving') : t('wb.save_btn')}
          </button>
        </div>
      </div>

      {cropModal && (
        <ImageCropModal
          file={cropModal.file}
          aspect={getCropConfig(cropModal.type, cropModal.index).aspect}
          title={getCropConfig(cropModal.type, cropModal.index).title}
          recommendedSize={getCropConfig(cropModal.type, cropModal.index).recommendedSize}
          onComplete={handleCropComplete}
          onCancel={() => setCropModal(null)}
        />
      )}
    </div>
  )
}

export default WebsiteBuilder